<template>
  <v-layout v-if="canRenderModifier" wrap>
    <v-flex class="xs6 md2 pa-2">
      <v-select
        v-model="modifier.attributes.unit"
        :items="modifierItems"
        item-text="name"
        item-value="value"
        label="When"
        @input="updateEquals"
      />
    </v-flex>

    <template v-if="equalsModifier">
      <v-flex class="xs6 md2 pa-2">
        <v-text-field
          v-model="modifier.attributes.equals"
          label="Equals"
          placeholder="Add Value"
          persistent-hint
          :hint="modifierHint"
        />
      </v-flex>

      <v-flex class="xs1 md4 pa-2" />
    </template>
    <template v-else-if="modifier.attributes.unit === 'foundation'">
      <v-flex class="xs3 md2 pa-2">
        <v-select
          v-model="modifier.attributes.equals"
          :items="foundationItems"
          item-text="name"
          item-value="value"
          label="Equals"
        />
      </v-flex>
      <v-flex class="xs3 md4">&nbsp;</v-flex>
    </template>
    <template
      v-else-if="
        customField && customField.attributes.response_format === 'dropdown'
      "
    >
      <v-flex class="xs3 md2 pa-2">
        <v-select
          v-model="modifier.attributes.equals"
          :items="customFieldItems"
          label="Equals"
        />
      </v-flex>
      <v-flex class="xs3 md4">&nbsp;</v-flex>
    </template>
    <template
      v-else-if="
        customField && customField.attributes.response_format === 'checkbox'
      "
    >
      <v-flex class="xs3 md2 pa-2">
        <v-select
          v-model="modifier.attributes.equals"
          :items="customFieldCheckboxItems"
          item-text="name"
          item-value="value"
          label="Equals"
        />
      </v-flex>
      <v-flex class="xs3 md4">&nbsp;</v-flex>
    </template>
    <template v-else>
      <v-flex v-if="displayNumberOptions" class="xs6 md2 pa-2">
        <v-select
          :items="numberItems"
          item-text="name"
          item-value="value"
          v-model="modifier.attributes.equals"
          label="Type"
        />
      </v-flex>

      <template v-if="modifier.attributes.equals === 'range'">
        <v-flex
          class="md2 pa-2"
          :class="{ xs6: displayNumberOptions, xs3: !displayNumberOptions }"
        >
          <v-text-field
            :error="errors.min && errors.min.length > 0"
            :error-messages="errors.min"
            type="number"
            min="0"
            step="1"
            v-model="modifier.attributes.min"
            label="Greater than (&gt;)"
            placeholder="0"
          />
        </v-flex>

        <v-flex
          class="md2 pa-2"
          :class="{ xs6: displayNumberOptions, xs3: !displayNumberOptions }"
        >
          <v-text-field
            :error="errors.max && errors.max.length > 0"
            :error-messages="errors.max"
            type="number"
            min="0"
            step="1"
            v-model="modifier.attributes.max"
            label="Less than or Equal to (&lt;=)"
            placeholder="0"
          />
        </v-flex>

        <v-flex v-if="!displayNumberOptions" class="xs1 md2 pa-2" />
      </template>

      <template
        v-else-if="
          modifier.attributes.equals === 'per unit over' && displayNumberOptions
        "
      >
        <v-flex class="md2 pa-2" :class="xs6">
          <v-text-field
            :error="errors.min && errors.min.length > 0"
            :error-messages="errors.min"
            type="number"
            min="0"
            step="1"
            v-model="modifier.attributes.min"
            label="Greater than (&gt;)"
            placeholder="0"
          />
        </v-flex>

        <v-flex class="md2 pa-2 xs6" />
      </template>
      <v-flex v-else class="xs1 md4">&nbsp;</v-flex>
    </template>

    <v-flex
      class="xs1 valign-wrapper pa-2 offset-sm0"
      :class="{
        'offset-xs1':
          displayNumberOptions && modifier.attributes.equals === 'range',
      }"
    >
      <div class="fa fa-chevron-right light" />
    </v-flex>

    <v-flex class="xs4 md1 pa-2">
      <v-text-field
        v-if="hasPayments"
        type="number"
        min="0.00"
        step="0.01"
        v-model="modifier.attributes.amount"
        label="Add Fee"
        placeholder="0.00"
      />
    </v-flex>

    <v-flex class="xs4 md1 pa-2">
      <v-text-field
        type="number"
        min="0.0"
        step="0.25"
        v-model="modifier.attributes.duration"
        label="Add Hours"
        placeholder="0"
      />
    </v-flex>

    <v-flex class="xs2 md1 valign-wrapper pa-2">
      <div class="btn-blank btn-flat btn-small danger" @click="deleteModifier">
        <div class="fa fa-trash" />
      </div>
    </v-flex>
  </v-layout>
</template>

<script>
import { debounce } from 'lodash'
import { mapState } from 'vuex'

export default {
  computed: {
    ...mapState('services', ['company', 'customFields']),
    baseModifierItems() {
      return [
        { name: 'Sq. Ft.', value: 'sqft', numberOptions: true },
        { name: 'Square meters (m²)', value: 'm2', numberOptions: true },
        { name: 'Age of Home', value: 'age' },
        { name: 'Year Built', value: 'year' },
        { name: 'Zip/Postal Code', value: 'zip' },
        {
          name: this.company.attributes.settings.county_label || 'County',
          value: 'county',
          visible: !!this.company.attributes.settings.county_data,
        },
        { name: 'Foundation', value: 'foundation' },
        {
          name: 'Miles away from company address',
          value: 'mileage',
          numberOptions: true,
        },
        {
          name: 'Kilometers away from company address',
          value: 'kilometrage',
          numberOptions: true,
        },
        {
          name: "Miles from inspector's home address",
          value: 'miles_from_home',
          numberOptions: true,
        },
        {
          name: "Kilometers from the inspector's home address",
          value: 'kilometers_from_home',
          numberOptions: true,
        },
      ].filter(({ visible }) => visible !== false)
    },
    canRenderModifier() {
      if (this.modifier.attributes.unit === 'county') {
        return this.company.attributes.settings.county_data
      }

      return true
    },
    customField() {
      if (this.modifier.attributes.custom_field_template_id) {
        return this.customFields.find((cf) => {
          return (
            cf.attributes.id ===
            this.modifier.attributes.custom_field_template_id
          )
        })
      } else {
        return null
      }
    },
    customFieldCheckboxItems() {
      if (
        this.customField &&
        this.customField.attributes.response_format === 'checkbox'
      ) {
        return [
          { name: 'Checked', value: 'true' },
          { name: 'Unchecked', value: 'false' },
        ]
      } else {
        return []
      }
    },
    customFieldItems() {
      if (
        this.customField &&
        this.customField.attributes.response_format === 'dropdown'
      ) {
        return this.customField.attributes.choices.split(',')
      } else {
        return []
      }
    },
    displayNumberOptions() {
      return (
        this.modifierItems.find((i) => {
          return this.modifier.attributes.unit === i.value
        }) || {}
      ).numberOptions
    },
    equalsModifier() {
      if (['zip', 'county'].includes(this.modifier.attributes.unit)) return true
      if (
        this.customField &&
        ['text', 'paragraph'].includes(
          this.customField.attributes.response_format
        )
      )
        return true
      return false
    },
    foundationItems() {
      return [
        { name: 'Basement', value: 'basement' },
        { name: 'Slab', value: 'slab' },
        { name: 'Crawlspace', value: 'crawlspace' },
      ]
    },
    hasPayments() {
      return this.company.attributes.settings.enable_payments
    },
    modifierHint() {
      if (['zip', 'county'].includes(this.modifier.attributes.unit)) {
        return 'add multiple by separating them with a comma'
      }

      return ''
    },
    modifierItems() {
      let items = this.baseModifierItems
      if (this.selectableCustomFields.length > 0) {
        items.push({ name: '- Custom Fields -', value: null, disabled: true })
        this.selectableCustomFields.forEach((cf) => {
          items.push({
            name: cf.attributes.name,
            value: `custom-field-template:${cf.id}`,
            numberOptions: cf.attributes.response_format === 'number',
          })
        })
      }

      return items
    },
    numberItems() {
      return [
        { name: 'Range', value: 'range' },
        { name: 'Per Unit', value: 'per unit' },
        { name: 'Per Unit Over', value: 'per unit over' },
      ]
    },
    range() {
      if (this.modifier.attributes.equals === 'range') {
        return {
          min: parseInt(this.modifier.attributes.min),
          max: parseInt(this.modifier.attributes.max),
        }
      } else {
        return {}
      }
    },
    selectableCustomFields() {
      return this.customFields.filter((cf) => {
        return ['text', 'number', 'dropdown', 'checkbox'].includes(
          cf.attributes.response_format
        )
      })
    },
  },
  data() {
    return {
      errors: {},
    }
  },
  methods: {
    deleteModifier() {
      this.$store.dispatch('services/deleteModifier', this.modifier.id)
    },
    updateEquals() {
      if (
        !this.displayNumberOptions &&
        !['age', 'year'].includes(this.modifier.attributes.unit)
      ) {
        this.modifier.attributes.equals = ''
      } else {
        this.modifier.attributes.equals = 'range'
      }
    },
    valid() {
      let errs = {}
      if (this.modifier.attributes.equals === 'range') {
        if (!this.range.max)
          errs.max = ["Your modifier won't work without this being set"]
        if (!this.range.min)
          errs.min = ["Your modifier won't work without this being set"]
        if (this.range.min && this.range.max && this.range.min > this.range.max)
          errs.min = [
            "This value can't be larger than your 'Less than or Equal' to value",
          ]
        if (this.range.min && this.range.max && this.range.min > this.range.max)
          errs.max = [
            "This value can't be lower than your 'Greater than' value",
          ]
      }

      this.errors = errs
      return Object.keys(this.errors).length === 0
    },
  },
  watch: {
    modifier: {
      handler: debounce(function (newVal) {
        if (newVal.attributes.unit.includes('custom-field-template')) {
          newVal.attributes.custom_field_template_id = parseInt(
            newVal.attributes.unit.split(':')[1]
          )
        } else {
          newVal.attributes.custom_field_template_id = null
        }

        if (this.valid()) {
          this.$store.dispatch('services/updateServiceModifier', newVal)
        }
      }, 500),
      deep: true,
    },
  },
  props: ['modifier'],
}
</script>

<style></style>
